/*
 * FOR SALE — mouvement opt-in (zéro dépendance).
 *
 * [data-fs-tilt]   Inclinaison 3D ≤ 6° (valeur facultative : data-fs-tilt="4") et reflet.
 *                  Uniquement pointeur fin + mouvement complet. Le module n'écrit que des
 *                  variables CSS sur l'élément ; la feuille de style les consomme :
 *                    --fs-tilt-x, --fs-tilt-y   angles (deg) pour rotateX / rotateY
 *                    --fs-glare                 opacité du reflet (0 → 1)
 *                    --fs-glare-x, --fs-glare-y position du reflet (%)
 *                  data-fs-tilt-active est présent pendant l'interaction.
 *                  ex. transform: perspective(900px) rotateX(var(--fs-tilt-x, 0deg)) rotateY(var(--fs-tilt-y, 0deg));
 *
 * [data-fs-reveal] Apparition au défilement. Sans JS, le contenu est visible.
 *                  <html data-fs-reveal-mode="timeline|observer|off"> :
 *                  - timeline : le CSS utilise animation-timeline: view() (aucun JS par élément) ;
 *                  - observer : repli IntersectionObserver, la classe .is-revealed est ajoutée ;
 *                    le CSS ne masque que html[data-fs-reveal-mode="observer"] [data-fs-reveal]:not(.is-revealed) ;
 *                  - off : mouvement réduit ou API absente, tout reçoit .is-revealed.
 *                  Les éléments visibles au démarrage sont révélés immédiatement (pas de flash).
 *
 * [data-fs-space]  Calque de fond du layout (fond « carte spatiale ») : parallaxe des plans [data-fs-space-layer]
 *                  (bornés, ou bouclés avec data-fs-space-loop).
 *                  <html data-fs-space="timeline|script|static"> :
 *                  - timeline : le CSS (fs-motion.css) anime les plans avec animation-timeline: scroll() ; aucun script ;
 *                  - script : repli, transform écrit dans requestAnimationFrame, écouteur de défilement passif,
 *                    rien quand l'onglet est caché ;
 *                  - static : mouvement réduit, écran < 768 px ou calque absent : plans immobiles.
 *                  Facteur, course et tuile lus sur chaque plan (--fs-space-f, --fs-space-max, --fs-space-tile) :
 *                  une seule source, le CSS.
 *                  html[data-fs-space-ready] après l'événement load : le CSS n'applique qu'alors les images
 *                  secondaires (plan intermédiaire), chargées sans concurrencer le héros.
 *                  Variante du jeu : data-fs-universe du calque suit l'événement fs:universe-change (for-sale.js) ;
 *                  la valeur initiale est posée par le script en ligne du layout, avant le premier rendu.
 *
 * [data-fs-rise]   Apparition au défilement, finie (440 ms), décalée de 60 ms par élément d'un même lot (4 pas au
 *                  plus). Sans script, ou mouvement réduit : tout est visible. Masquage seulement quand
 *                  html[data-fs-rise="on"] est posé, APRÈS avoir révélé ce qui est déjà à l'écran (aucun clignotement).
 *
 * <dialog>         Origine de l'entrée (fs-ui-dialog.css) : à l'ouverture, --fs-dialog-ox / --fs-dialog-oy prennent la
 *                  position du déclencheur (dernier appui du pointeur, sinon élément focalisé) dans la boîte du dialogue :
 *                  la fenêtre naît du bouton. Déclencheur absent, hors écran ou volet : centre (aucune variable).
 *
 * Zone commerce : un élément [data-fs-tilt], [data-fs-reveal] ou [data-fs-rise] situé DANS un [data-fs-commerce],
 * qui EST un [data-fs-commerce], ou qui en CONTIENT un, est ignoré : aucune variable écrite,
 * .is-revealed posé d'emblée et attribut data-fs-tilt-static / data-fs-reveal-static ajouté.
 * Le CSS doit exclure ces éléments (:not([data-fs-reveal-static]), :not([data-fs-tilt-static]))
 * afin que prix, disponibilité et bouton d'achat ne soient jamais transformés, voilés ou retardés.
 */

export const TILT_MAX_DEG = 6;
export const COMMERCE_SELECTOR = '[data-fs-commerce]';
export const SPACE_MIN_WIDTH = 768;
export const RISE_DURATION_MS = 440; /* = --fs-rise-dur (fs-motion.css) */
export const RISE_STEP_MS = 60;
export const RISE_MAX_STEPS = 4;
const UNIVERSE_ID = /^[a-z0-9-]{1,40}$/;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** true si l'élément touche une zone commerce (dedans, lui-même ou ancêtre d'une zone). */
export function isCommerceProtected(element) {
  if (!element) return true;
  try {
    return Boolean(element.closest(COMMERCE_SELECTOR) || element.querySelector(COMMERCE_SELECTOR));
  } catch {
    return true;
  }
}

/** Amplitude demandée par data-fs-tilt, bornée à ]0, 6]. */
export function tiltLimit(value) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? Math.min(n, TILT_MAX_DEG) : TILT_MAX_DEG;
}

/**
 * Angles et reflet pour un pointeur (x, y) sur un rectangle.
 * La zone sous le pointeur « s'enfonce » : rx pour rotateX, ry pour rotateY.
 */
export function computeTilt(x, y, rect, maxDeg = TILT_MAX_DEG) {
  const limit = tiltLimit(maxDeg);
  const width = rect && rect.width > 0 ? rect.width : 1;
  const height = rect && rect.height > 0 ? rect.height : 1;
  const nx = clamp((x - ((rect && rect.left) || 0)) / width, 0, 1);
  const ny = clamp((y - ((rect && rect.top) || 0)) / height, 0, 1);
  return {
    rx: (0.5 - ny) * 2 * limit,
    ry: (nx - 0.5) * 2 * limit,
    glareX: nx * 100,
    glareY: ny * 100,
    glare: clamp(0.25 + Math.hypot(nx - 0.5, ny - 0.5) * 0.9, 0, 0.7)
  };
}

export function tiltAllowed({ finePointer, reducedMotion } = {}) {
  return Boolean(finePointer) && !reducedMotion;
}

export function chooseRevealMode({ reducedMotion, viewTimeline, intersectionObserver } = {}) {
  if (reducedMotion) return 'off';
  if (viewTimeline) return 'timeline';
  return intersectionObserver ? 'observer' : 'off';
}

/**
 * Décalage vertical d'un plan du fond (px, ≤ 0), au centième de pixel :
 *  - plan bouclé (tile > 0) : -(défilement × f) modulo la tuile ;
 *  - plan borné : -min(défilement × f, max).
 */
export function spaceOffset(scrollY, factor, { tile = 0, max = 0 } = {}) {
  const travel = Math.max(0, Number(scrollY) || 0) * Math.max(0, Number(factor) || 0);
  const shift = tile > 0 ? travel % tile : Math.min(travel, Math.max(0, max));
  return -Math.round(shift * 100) / 100 || 0;
}

export function chooseSpaceMode({ hasLayer, reducedMotion, wide, scrollTimeline } = {}) {
  if (!hasLayer || reducedMotion || !wide) return 'static';
  return scrollTimeline ? 'timeline' : 'script';
}

/**
 * Origine d'une fenêtre qui naît de son déclencheur : centre du déclencheur ramené dans la boîte du dialogue (px).
 * null si l'un des deux rectangles est vide.
 */
export function dialogOrigin(trigger, box) {
  if (!trigger || !box || !(trigger.width > 0) || !(box.width > 0) || !(box.height > 0)) return null;
  return {
    x: Math.round(clamp(trigger.left + trigger.width / 2 - box.left, 0, box.width)),
    y: Math.round(clamp(trigger.top + (trigger.height || 0) / 2 - box.top, 0, box.height))
  };
}

/** Délai du n-ième élément d'un lot d'apparitions (ordre de lecture). */
export function riseDelay(index) {
  const n = Number.isInteger(index) && index > 0 ? index : 0;
  return Math.min(n, RISE_MAX_STEPS) * RISE_STEP_MS;
}

/** Ordre de lecture : de haut en bas, puis de gauche à droite (même ligne à 8 px près). */
export function readingOrder(a, b) {
  return Math.abs(a.top - b.top) > 8 ? a.top - b.top : a.left - b.left;
}

/* ---------- Navigateur ---------- */

/* Parallaxe du fond « carte spatiale ». Renvoie setup, à rappeler quand la préférence de mouvement change. */
function bootSpace(win, doc, reduced) {
  const html = doc.documentElement;
  const space = doc.querySelector('[data-fs-space]');
  const layers = space ? [...space.querySelectorAll('[data-fs-space-layer]')] : [];
  const wideQuery = win.matchMedia ? win.matchMedia(`(min-width: ${SPACE_MIN_WIDTH}px)`) : null;
  const timeline = (() => {
    try { return Boolean(win.CSS && win.CSS.supports && win.CSS.supports('animation-timeline: scroll()')); } catch { return false; }
  })();
  let mode = '';
  let frame = 0;
  let plan = [];

  const paint = () => {
    frame = 0;
    const y = win.scrollY;
    /* Écritures seules : aucune lecture de mise en page dans l'image. */
    plan.forEach(({ layer, factor, shape }) => { layer.style.transform = `translate3d(0, ${spaceOffset(y, factor, shape)}px, 0)`; });
  };
  const schedule = () => { if (!frame && !doc.hidden) frame = win.requestAnimationFrame(paint); };
  const read = () => {
    plan = layers.map((layer) => {
      const style = win.getComputedStyle(layer);
      const tile = layer.hasAttribute('data-fs-space-loop') ? parseFloat(style.getPropertyValue('--fs-space-tile')) || 0 : 0;
      return { layer, factor: parseFloat(style.getPropertyValue('--fs-space-f')) || 0, shape: { tile, max: parseFloat(style.getPropertyValue('--fs-space-max')) || 0 } };
    }).filter(({ factor }) => factor > 0);
  };

  const setup = () => {
    const next = chooseSpaceMode({ hasLayer: layers.length > 0, reducedMotion: reduced(), wide: Boolean(wideQuery && wideQuery.matches), scrollTimeline: timeline });
    if (next === mode) return;
    win.removeEventListener('scroll', schedule);
    if (frame) win.cancelAnimationFrame(frame);
    frame = 0;
    layers.forEach((layer) => layer.style.removeProperty('transform'));
    mode = next;
    html.setAttribute('data-fs-space', mode);
    if (mode !== 'script') return;
    read();
    win.addEventListener('scroll', schedule, { passive: true });
    paint();
  };

  /* Images secondaires après load ; variante du jeu affiché (écran d'un jeu du catalogue seulement). */
  const ready = () => html.setAttribute('data-fs-space-ready', '');
  if (doc.readyState === 'complete') ready(); else win.addEventListener('load', ready, { once: true });
  doc.addEventListener('fs:universe-change', (event) => {
    const id = event.detail && event.detail.id;
    if (space && typeof id === 'string' && UNIVERSE_ID.test(id)) space.setAttribute('data-fs-universe', id);
  });

  /* Onglet de retour au premier plan, page restaurée du cache (bfcache) : une image pour se recaler. */
  doc.addEventListener('visibilitychange', () => { if (mode === 'script' && !doc.hidden) schedule(); });
  win.addEventListener('pageshow', () => { if (mode === 'script') schedule(); });
  if (wideQuery && wideQuery.addEventListener) wideQuery.addEventListener('change', setup);
  setup();
  return setup;
}

/* Apparition au défilement [data-fs-rise]. */
function bootRise(win, doc, reduced) {
  const html = doc.documentElement;
  let observer = null;

  const settle = (element) => {
    element.classList.remove('is-rising');
    element.style.removeProperty('--fs-rise-delay');
  };
  const show = (entries) => {
    entries
      .map((element) => ({ element, rect: element.getBoundingClientRect() }))
      .sort((a, b) => readingOrder(a.rect, b.rect))
      .forEach(({ element }, index) => {
        element.style.setProperty('--fs-rise-delay', `${riseDelay(index)}ms`);
        element.classList.add('is-rising', 'is-risen');
        /* Transition finie : l'élément retrouve ses propres transitions (survol des cartes…). Minuterie plutôt que
           transitionend, qui ne vient pas si l'élément est masqué entre-temps. */
        win.setTimeout(() => settle(element), RISE_DURATION_MS + riseDelay(index) + 60);
      });
  };

  const register = (elements) => {
    const pending = [];
    elements.forEach((element) => {
      if (element.classList.contains('is-risen')) return;
      if (isCommerceProtected(element)) {
        element.setAttribute('data-fs-reveal-static', '');
        element.classList.add('is-risen');
      } else if (!observer) element.classList.add('is-risen');
      else pending.push(element);
    });
    if (!pending.length) return;
    const viewport = win.innerHeight || 0;
    const rects = pending.map((element) => element.getBoundingClientRect());
    pending.forEach((element, index) => {
      /* Déjà à l'écran (ou au-dessus, après un saut d'ancre) : visible tout de suite, sans transition. */
      if (rects[index].top < viewport) element.classList.add('is-risen');
      else observer.observe(element);
    });
  };

  const setup = () => {
    if (observer) observer.disconnect();
    observer = null;
    const on = !reduced() && typeof win.IntersectionObserver === 'function';
    if (on) {
      observer = new win.IntersectionObserver((records) => {
        const entering = records.filter((record) => record.isIntersecting).map((record) => record.target);
        entering.forEach((element) => observer.unobserve(element));
        if (entering.length) show(entering);
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    }
    register([...doc.querySelectorAll('[data-fs-rise]')]);
    html.setAttribute('data-fs-rise', on ? 'on' : 'off');
  };
  setup();
  return { setup, register };
}

/* Origine des dialogues : posée juste après showModal(), avant la première image (microtâche). */
function bootDialogs(win, doc) {
  const TRIGGER = 'button, a[href], summary, label, [role="button"], [tabindex]';
  let pressed = null;
  let pressedAt = 0;
  doc.addEventListener('pointerdown', (event) => {
    pressed = event.target && event.target.closest ? event.target.closest(TRIGGER) : null;
    pressedAt = Date.now();
  }, { capture: true, passive: true });
  doc.addEventListener('beforetoggle', (event) => {
    const dialog = event.target;
    if (!dialog || dialog.tagName !== 'DIALOG' || event.newState !== 'open') return;
    const recent = pressed && pressed.isConnected && Date.now() - pressedAt < 1500 ? pressed : null;
    const trigger = recent || (doc.activeElement !== doc.body ? doc.activeElement : null);
    dialog.style.removeProperty('--fs-dialog-ox');
    dialog.style.removeProperty('--fs-dialog-oy');
    if (!trigger || dialog.contains(trigger) || dialog.classList.contains('fs-dialog--sheet')) return;
    win.queueMicrotask(() => {
      if (!dialog.open) return;
      const origin = dialogOrigin(trigger.getBoundingClientRect(), dialog.getBoundingClientRect());
      if (!origin) return;
      dialog.style.setProperty('--fs-dialog-ox', `${origin.x}px`);
      dialog.style.setProperty('--fs-dialog-oy', `${origin.y}px`);
    });
  }, true);
}

function boot(win, doc) {
  const html = doc.documentElement;
  const query = (text) => (win.matchMedia ? win.matchMedia(text) : null);
  const reduceQuery = query('(prefers-reduced-motion: reduce)');
  const fineQuery = query('(hover: hover) and (pointer: fine)');
  /* Mouvement réduit : préférence du système, ou html[data-fs-motion="reduced"] posé par le moteur des univers. */
  const reduced = () => Boolean(reduceQuery && reduceQuery.matches) || html.getAttribute('data-fs-motion') === 'reduced';
  const supportsTimeline = (() => {
    try {
      return Boolean(win.CSS && win.CSS.supports && win.CSS.supports('animation-timeline: view()'));
    } catch {
      return false;
    }
  })();

  /* ---- Reveal ---- */
  let mode = 'off';
  let observer = null;

  const markStatic = (element) => {
    element.setAttribute('data-fs-reveal-static', '');
    element.classList.add('is-revealed');
  };

  const register = (elements) => {
    const pending = [];
    elements.forEach((element) => {
      if (element.classList.contains('is-revealed')) return;
      if (isCommerceProtected(element)) markStatic(element);
      else if (mode !== 'observer') {
        if (mode === 'off') element.classList.add('is-revealed');
      } else pending.push(element);
    });
    if (!pending.length) return;
    const viewport = win.innerHeight || 0;
    const rects = pending.map((element) => element.getBoundingClientRect());
    pending.forEach((element, index) => {
      const rect = rects[index];
      if (rect.bottom > 0 && rect.top < viewport) element.classList.add('is-revealed');
      else observer.observe(element);
    });
  };

  const collect = (root) => {
    const found = [];
    if (root.nodeType !== 1) return found;
    if (root.matches('[data-fs-reveal]')) found.push(root);
    root.querySelectorAll('[data-fs-reveal]').forEach((element) => found.push(element));
    return found;
  };

  const setupReveal = () => {
    if (observer) observer.disconnect();
    observer = null;
    mode = chooseRevealMode({ reducedMotion: reduced(), viewTimeline: supportsTimeline, intersectionObserver: typeof win.IntersectionObserver === 'function' });
    if (mode === 'observer') {
      observer = new win.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    }
    register(collect(doc.body || html));
    html.setAttribute('data-fs-reveal-mode', mode);
  };

  /* ---- Tilt ---- */
  const tiltState = new WeakMap();
  const animating = new Set();
  let tiltOn = false;
  let raf = 0;
  let active = null;
  let lastTime = 0;

  const eligible = (element) => {
    let state = tiltState.get(element);
    if (!state) {
      const protectedZone = isCommerceProtected(element);
      if (protectedZone) element.setAttribute('data-fs-tilt-static', '');
      state = { protectedZone, rx: 0, ry: 0, glare: 0, tx: 0, ty: 0, tglare: 0, gx: 50, gy: 50, px: 0, py: 0, hover: false };
      tiltState.set(element, state);
    }
    return state.protectedZone ? null : state;
  };

  const clear = (element) => {
    ['--fs-tilt-x', '--fs-tilt-y', '--fs-glare', '--fs-glare-x', '--fs-glare-y'].forEach((name) => element.style.removeProperty(name));
    element.removeAttribute('data-fs-tilt-active');
  };

  const step = (now) => {
    raf = 0;
    const dt = lastTime ? Math.min(now - lastTime, 64) : 16;
    lastTime = now;
    const k = 1 - Math.exp(-dt / 90);
    animating.forEach((element) => {
      const state = tiltState.get(element);
      if (!state) {
        animating.delete(element);
        return;
      }
      if (state.hover) {
        /* Lecture de mise en page avant toute écriture de la frame. */
        const target = computeTilt(state.px, state.py, element.getBoundingClientRect(), element.getAttribute('data-fs-tilt'));
        state.tx = target.rx;
        state.ty = target.ry;
        state.tglare = target.glare;
        state.gx = target.glareX;
        state.gy = target.glareY;
      }
      state.rx += (state.tx - state.rx) * k;
      state.ry += (state.ty - state.ry) * k;
      state.glare += (state.tglare - state.glare) * k;
      if (!state.hover && Math.abs(state.rx) < 0.02 && Math.abs(state.ry) < 0.02 && state.glare < 0.01) {
        state.rx = 0;
        state.ry = 0;
        state.glare = 0;
        clear(element);
        animating.delete(element);
        return;
      }
      element.style.setProperty('--fs-tilt-x', `${state.rx.toFixed(2)}deg`);
      element.style.setProperty('--fs-tilt-y', `${state.ry.toFixed(2)}deg`);
      element.style.setProperty('--fs-glare', state.glare.toFixed(3));
      element.style.setProperty('--fs-glare-x', `${state.gx.toFixed(1)}%`);
      element.style.setProperty('--fs-glare-y', `${state.gy.toFixed(1)}%`);
    });
    if (animating.size) raf = win.requestAnimationFrame(step);
    else lastTime = 0;
  };

  const kick = () => {
    if (!raf) raf = win.requestAnimationFrame(step);
  };

  const release = (element) => {
    const state = element && tiltState.get(element);
    if (!state) return;
    state.hover = false;
    state.tx = 0;
    state.ty = 0;
    state.tglare = 0;
    kick();
  };

  const onMove = (event) => {
    if (!tiltOn || event.pointerType === 'touch') return;
    const target = event.target && event.target.closest ? event.target.closest('[data-fs-tilt]') : null;
    if (target !== active) {
      if (active) release(active);
      active = null;
      if (target && eligible(target)) {
        active = target;
        target.setAttribute('data-fs-tilt-active', '');
      }
    }
    if (!active) return;
    const state = tiltState.get(active);
    state.hover = true;
    state.px = event.clientX;
    state.py = event.clientY;
    animating.add(active);
    kick();
  };

  const onLeave = (event) => {
    if (active && (!event.relatedTarget || !active.contains(event.relatedTarget))) {
      release(active);
      active = null;
    }
  };

  const resetTilt = () => {
    if (raf) win.cancelAnimationFrame(raf);
    raf = 0;
    lastTime = 0;
    animating.forEach((element) => {
      const state = tiltState.get(element);
      if (state) Object.assign(state, { rx: 0, ry: 0, glare: 0, tx: 0, ty: 0, tglare: 0, hover: false });
      clear(element);
    });
    animating.clear();
    active = null;
  };

  const setupTilt = () => {
    tiltOn = tiltAllowed({ finePointer: Boolean(fineQuery && fineQuery.matches), reducedMotion: reduced() });
    if (!tiltOn) resetTilt();
  };

  doc.addEventListener('pointermove', onMove, { passive: true });
  doc.addEventListener('pointerout', onLeave, { passive: true });

  bootDialogs(win, doc);
  const setupSpace = bootSpace(win, doc, reduced);
  const rise = bootRise(win, doc, reduced);

  const onPreferenceChange = () => {
    setupTilt();
    setupReveal();
    setupSpace();
    rise.setup();
  };
  [reduceQuery, fineQuery].forEach((mq) => {
    if (mq && mq.addEventListener) mq.addEventListener('change', onPreferenceChange);
  });

  setupTilt();
  setupReveal();

  /* Contenus rendus après coup (catalogue chargé dynamiquement, avis paginés). */
  if (typeof win.MutationObserver === 'function' && doc.body) {
    new win.MutationObserver((records) => {
      const found = [];
      const rising = [];
      records.forEach((record) => record.addedNodes.forEach((node) => {
        found.push(...collect(node));
        if (node.nodeType !== 1) return;
        if (node.matches('[data-fs-rise]')) rising.push(node);
        rising.push(...node.querySelectorAll('[data-fs-rise]'));
      }));
      if (found.length) register(found);
      if (rising.length) rise.register(rising);
    }).observe(doc.body, { childList: true, subtree: true });
  }

  win.FSMotion = Object.freeze({
    /** Ré-examine le document (ex. après un changement d'attribut data-fs-commerce). */
    refresh() {
      setupTilt();
      setupReveal();
      setupSpace();
      rise.setup();
    }
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.FSMotion) {
  const run = () => boot(window, document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
}
